import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiAlertCircle, FiCheckCircle, FiLoader, FiSearch } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/accessibility-check';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

function ScoreRing({ value, label, size = 120 }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = value != null ? circ - (circ * value) / 100 : circ;
  const color = value == null ? '#94a3b8' : value >= 90 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
          className="fill-slate-900 font-bold" style={{ fontSize: size * 0.26 }}>
          {value != null ? `${value}%` : '—'}
        </text>
      </svg>
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</span>
    </div>
  );
}

function ScanProgressBar() {
  return (
    <div className="w-full max-w-xl">
      <div className="h-2 rounded-full bg-slate-200 overflow-hidden relative" role="progressbar" aria-valuetext="Scan in progress">
        <m.div
          className="absolute top-0 bottom-0 w-[38%] rounded-full bg-indigo-600"
          initial={{ left: '-38%' }}
          animate={{ left: '100%' }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'linear' }}
        />
      </div>
    </div>
  );
}

export default function AccessibilityCheckPage() {
  const urlFieldId = useId();
  const helperId = `${urlFieldId}-helper`;
  const statusId = `${urlFieldId}-status`;
  const resultsRef = useRef(null);

  const [urlInput, setUrlInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = 'Free accessibility scan (PageSpeed / Lighthouse) | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) {
      desc.setAttribute(
        'content',
        'Free accessibility check powered by Google PageSpeed Insights: mobile Lighthouse accessibility score and actionable audit failures. UK-based WebCodeCraft.'
      );
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const pageTitle = 'Free accessibility scan (PageSpeed / Lighthouse) | WebCodeCraft';
    const pageDesc = 'Free accessibility check powered by Google PageSpeed Insights: mobile Lighthouse accessibility score and actionable audit failures.';
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);
    if (ogUrl) ogUrl.setAttribute('content', CANONICAL);

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Free accessibility scan',
      url: CANONICAL,
      description: pageDesc,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'accessibility-check');
    document.head.appendChild(ld);

    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc && prevDesc != null) desc.setAttribute('content', prevDesc);
      ld.remove();
    };
  }, []);

  const runScan = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setData(null);
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setError('Enter a URL to scan.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/accessibility-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        setError(typeof json.error === 'string' ? json.error : 'Scan failed.');
        return;
      }
      setData(json);
      requestAnimationFrame(() => { resultsRef.current?.focus(); });
    } catch {
      setError('Network error. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [urlInput]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />

      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="a11y-scan-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/website-tax-calculator" className="underline underline-offset-2 hover:text-indigo-800">Tools</Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">/</span>
              <span className="text-slate-700">Accessibility scan</span>
            </p>
            <h1 id="a11y-scan-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              Accessibility check
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6" id={helperId}>
              Paste a <strong>public</strong> URL. We call the{' '}
              <a href="https://developers.google.com/speed/docs/insights/v5/about" className="text-indigo-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">PageSpeed Insights API</a>
              {' '}(Lighthouse, mobile) and show your <strong>accessibility score</strong> plus{' '}
              <strong>action items</strong> for audits that failed (score 0). Scans often take 15–30 seconds.
            </p>

            <form onSubmit={runScan} className="space-y-4" noValidate>
              <div>
                <label htmlFor={urlFieldId} className="block text-sm font-semibold text-slate-800 mb-1.5">Page URL</label>
                <input
                  id={urlFieldId}
                  type="url"
                  name="url"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://example.com"
                  value={urlInput}
                  onChange={(ev) => setUrlInput(ev.target.value)}
                  className="form-input w-full max-w-xl"
                  aria-describedby={helperId}
                  aria-invalid={error ? 'true' : 'false'}
                  disabled={loading}
                />
              </div>
              <m.button
                type="submit"
                className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
              >
                {loading
                  ? <><FiLoader className="animate-spin shrink-0" aria-hidden="true" />Scanning…</>
                  : <><FiSearch aria-hidden="true" />Run accessibility scan</>}
              </m.button>
            </form>

            <div id={statusId} role="status" aria-live="polite" className="mt-6 space-y-4 min-h-[1.25rem] text-sm">
              {error && (
                <p className="text-red-700 font-medium flex items-start gap-2">
                  <FiAlertCircle className="shrink-0 mt-0.5" aria-hidden="true" />{error}
                </p>
              )}
              {loading && (
                <div className="space-y-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-4">
                  <p className="text-slate-700 flex items-center gap-2">
                    <FiLoader className="animate-spin shrink-0 text-indigo-600" aria-hidden="true" />
                    <span>Scanning with Google PageSpeed Insights — Lighthouse is rendering your page. This usually takes 15–30 seconds.</span>
                  </p>
                  <ScanProgressBar />
                </div>
              )}
            </div>
          </m.div>
        </section>

        {data?.ok && (
          <section
            ref={resultsRef}
            tabIndex={-1}
            className="container mx-auto px-4 pb-16 max-w-3xl outline-none space-y-8"
            aria-label="Accessibility scan results"
          >
            <div className="flex flex-col items-center gap-3 py-2">
              <ScoreRing value={data.score} label="Accessibility score" size={140} />
              <p className="text-sm text-slate-500 text-center max-w-md">
                Lighthouse accessibility category (mobile). Based on data from Google’s PageSpeed Insights API.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Action items</h2>
              {!data.actionItems || data.actionItems.length === 0 ? (
                <p className="flex items-start gap-2 text-emerald-800 text-sm">
                  <FiCheckCircle className="shrink-0 mt-0.5" aria-hidden="true" />
                  <span>No failing audits in this run — all checked accessibility audits passed or were not applicable.</span>
                </p>
              ) : (
                <ol className="space-y-5 list-decimal list-inside marker:font-semibold marker:text-slate-800">
                  {data.actionItems.map((item) => (
                    <li key={item.id} className="pl-1">
                      <span className="font-semibold text-slate-900">{item.title}</span>
                      {item.description ? (
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed pl-0">{item.description}</p>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <p className="text-xs text-slate-500">
              Results reflect Lighthouse rules at the time of the scan.
            </p>
          </section>
        )}

        <Footer />
      </main>
    </div>
  );
}
